/**
 * BuildInfoBadge (iter237, iter239 stale-tab nudge) — quiet build-identity
 * readout pinned to the bottom of the admin sidebar.
 *
 * Shows the short SHA plus how long ago that commit was made, so "what's
 * actually live?" is answerable at a glance instead of by downloading bundle
 * chunks and grepping them.
 *
 * iter239 — Also detects a deploy happening UNDER an open admin tab. Polls
 * `GET /api/version` every 2 minutes (and immediately whenever the tab
 * regains focus, which is exactly when a returning admin needs to know),
 * compares the SHA against the one this tab loaded with, and if it moved it
 * turns amber with a one-click reload. A single toast fires on detection so
 * it's noticed once without nagging.
 *
 * Polling only runs while `document.visibilityState === 'visible'`, so
 * background tabs don't hammer the endpoint.
 *
 * Deliberately low-key otherwise: muted slate text, no card, no heading.
 * Hover for the full SHA / branch / commit time / uptime; click to copy the
 * full SHA.
 *
 * Surfaces `source` honestly — if a deployed image strips `.git` the endpoint
 * returns `source: "unavailable"` and this renders "version unknown" rather
 * than implying a SHA it doesn't have. Stale detection is skipped in that
 * case since there is no reliable SHA to compare.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GitCommitHorizontal, Check, Copy, AlertCircle, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import api from '@/lib/api';

const POLL_MS = 2 * 60 * 1000;

const relative = (iso) => {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
};

const BuildInfoBadge = () => {
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [incomingSha, setIncomingSha] = useState(null);   // newer SHA seen while this tab was open

  const aliveRef = useRef(true);
  const baselineShaRef = useRef(null);   // the SHA this tab started on
  const notifiedRef = useRef(false);

  // StrictMode runs effects setup -> cleanup -> setup, so this MUST re-arm on
  // setup. Only setting it false in cleanup leaves it permanently false after
  // mount and every load() bails silently. (Same trap as iter232's auto-play.)
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const load = useCallback(async (isPoll) => {
    try {
      const { data } = await api.get('/version');
      if (!aliveRef.current) return;
      const sha = data?.git?.sha || null;

      if (!isPoll) {
        setInfo(data);
        baselineShaRef.current = sha;
        return;
      }

      // Poll: only a real SHA move counts. A bare backend restart changes
      // started_at but not the commit, and must not nudge.
      if (sha && baselineShaRef.current && sha !== baselineShaRef.current) {
        setIncomingSha(sha);
        if (!notifiedRef.current) {
          notifiedRef.current = true;
          toast('New version deployed', {
            description: `${sha.slice(0, 8)} is now live — reload to pick it up.`,
            duration: 30000,
            action: { label: 'Reload', onClick: () => window.location.reload() },
          });
        }
      }
    } catch {
      if (aliveRef.current && !isPoll) setFailed(true);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    const id = setInterval(poll, POLL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [load]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  if (failed) {
    return (
      <div
        className="px-4 pt-3 text-[10px] text-slate-600 inline-flex items-center gap-1.5"
        data-testid="build-info-badge-error"
      >
        <AlertCircle className="h-3 w-3" />
        version unavailable
      </div>
    );
  }

  if (!info) {
    return (
      <div className="px-4 pt-3 text-[10px] text-slate-600" data-testid="build-info-badge-loading">
        …
      </div>
    );
  }

  const { git = {}, runtime = {} } = info;
  const known = git.source && git.source !== 'unavailable' && git.short_sha;
  const committed = relative(git.committed_at);

  // A deploy landed under this tab — take over the badge with an amber nudge.
  if (incomingSha) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full px-4 pt-3 pb-1 text-left text-[10px] text-amber-300/90 hover:text-amber-200 transition-colors inline-flex items-center gap-1.5"
              data-testid="build-info-stale"
            >
              <RefreshCw className="h-3 w-3 shrink-0" />
              <span>New version · reload</span>
              <span className="ml-auto font-mono text-amber-400/70" data-testid="build-info-incoming-sha">
                {incomingSha.slice(0, 8)}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs text-xs">
            <div className="space-y-1" data-testid="build-info-stale-tooltip">
              <div className="text-slate-200">A newer build is live on the server.</div>
              <div className="text-slate-400">
                this tab <span className="font-mono text-slate-200">{git.short_sha || '—'}</span>
                {' → '}server <span className="font-mono text-amber-300">{incomingSha.slice(0, 8)}</span>
              </div>
              <div className="text-slate-500">Click to reload.</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const copySha = async () => {
    if (!git.sha) return;
    try {
      await navigator.clipboard.writeText(git.sha);
      setCopied(true);
    } catch {
      /* clipboard blocked — non-critical */
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={copySha}
            disabled={!git.sha}
            className="group w-full px-4 pt-3 pb-1 text-left text-[10px] text-slate-500 hover:text-slate-300 transition-colors inline-flex items-center gap-1.5 disabled:hover:text-slate-500"
            data-testid="build-info-badge"
          >
            <GitCommitHorizontal className="h-3 w-3 shrink-0" />
            {known ? (
              <>
                <span className="font-mono tabular-nums" data-testid="build-info-sha">
                  {git.short_sha}
                </span>
                {committed && (
                  <span className="truncate text-slate-600" data-testid="build-info-age">
                    {committed}
                  </span>
                )}
                <span className="ml-auto shrink-0">
                  {copied
                    ? <Check className="h-3 w-3 text-emerald-400" data-testid="build-info-copied" />
                    : <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100" />}
                </span>
              </>
            ) : (
              <span data-testid="build-info-unknown">version unknown</span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs text-xs">
          <div className="space-y-1" data-testid="build-info-tooltip">
            {known ? (
              <>
                <div className="font-mono break-all text-[10px]">{git.sha}</div>
                <div className="text-slate-400">
                  branch <span className="text-slate-200">{git.branch || '—'}</span>
                  {' · '}via <span className="text-slate-200">{git.source}</span>
                </div>
                {git.committed_at && (
                  <div className="text-slate-400">committed {git.committed_at}</div>
                )}
                <div className="text-slate-500">Click to copy the full SHA</div>
              </>
            ) : (
              <div className="text-slate-300">
                No build identity available — this image has no <span className="font-mono">.git</span>.
                Bake a <span className="font-mono">build_info.json</span> at build time to populate it.
              </div>
            )}
            {runtime.started_at && (
              <div className="border-t border-slate-700 pt-1 text-slate-500">
                api up {relative(runtime.started_at) || '—'}
                {runtime.python ? ` · py ${runtime.python}` : ''}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BuildInfoBadge;
